import { useTranslation } from 'react-i18next';
import { Shield, FileText, Lock, Users, Database, Scale } from 'lucide-react';

export const DataProcessingAgreement = () => {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language?.startsWith('es');

  const content = {
    es: {
      title: "Acuerdo de Procesamiento de Datos (DPA)",
      subtitle: "Entre LokAlGrow (Procesador) y el Negocio (Responsable del Tratamiento)",
      lastUpdated: "Última actualización: Abril 2026",
      sections: [
        {
          icon: FileText,
          title: "1. Definiciones y Alcance",
          content: `Este Acuerdo de Procesamiento de Datos ("DPA") forma parte de los Términos de Servicio de LokAlGrow y establece las obligaciones de las partes con respecto al tratamiento de datos personales.

- "Responsable del Tratamiento": El negocio que utiliza los servicios de LokAlGrow y determina los fines y medios del tratamiento de los datos de sus clientes.
- "Procesador": LokAlGrow, S.L., que trata los datos personales en nombre del Responsable.
- "Datos Personales": Cualquier información relativa a una persona física identificada o identificable proporcionada por el Responsable a través de la plataforma.`
        },
        {
          icon: Database,
          title: "2. Datos que Procesamos",
          content: `En el contexto de los servicios de reactivación y club de cumpleaños, LokAlGrow procesa:

- Nombre y apellidos de los contactos
- Número de teléfono (WhatsApp/SMS)
- Dirección de correo electrónico (cuando se proporciona)
- Fecha de cumpleaños (para Club VIP)
- Historial de interacciones con el agente IA
- Estado de las conversaciones y reservas

Estos datos son proporcionados exclusivamente por el Negocio, quien declara tener base legal para su tratamiento.`
        },
        {
          icon: Lock,
          title: "3. Seguridad y Confidencialidad",
          content: `LokAlGrow implementa medidas técnicas y organizativas apropiadas para garantizar la seguridad de los datos:

- Cifrado de datos en tránsito (TLS 1.3) y en reposo
- Acceso restringido mediante autenticación de múltiples factores
- Segregación lógica de datos por negocio
- Copias de seguridad cifradas con retención de 30 días
- Auditorías de seguridad periódicas
- Personal con acuerdos de confidencialidad

Las conversaciones de cada campaña están aisladas y son accesibles únicamente por el Negocio propietario y el personal autorizado de LokAlGrow.`
        },
        {
          icon: Users,
          title: "4. Derechos de los Interesados",
          content: `LokAlGrow asistirá al Negocio en el cumplimiento de sus obligaciones respecto a los derechos de los interesados:

- Derecho de acceso: Proporcionaremos exportación de datos bajo petición
- Derecho de rectificación: El Negocio puede actualizar los datos desde su panel
- Derecho de supresión: Implementamos opt-out inmediato cuando un contacto lo solicita
- Derecho de oposición: Los contactos que expresan no querer ser contactados son marcados automáticamente como "No Contactar"

Cuando un contacto solicite la eliminación de sus datos, LokAlGrow lo procesará en un plazo máximo de 72 horas.`
        },
        {
          icon: Shield,
          title: "5. Subprocesadores",
          content: `LokAlGrow utiliza los siguientes subprocesadores para prestar sus servicios:

- Twilio Inc. (EE.UU.): Envío de SMS y WhatsApp - Certificación SOC 2
- Anthropic (EE.UU.): Procesamiento de lenguaje natural - Con cláusulas contractuales tipo
- MongoDB Atlas (UE/EE.UU.): Almacenamiento de datos - Certificación ISO 27001
- Brevo (Francia): Envío de emails - RGPD compliant

Las transferencias internacionales se realizan bajo el marco de Cláusulas Contractuales Tipo de la UE.`
        },
        {
          icon: Scale,
          title: "6. Duración y Terminación",
          content: `Este DPA permanecerá vigente mientras el Negocio utilice los servicios de LokAlGrow.

Tras la terminación del servicio:
- Los datos de contactos serán eliminados en un plazo de 90 días
- El Negocio puede solicitar una exportación completa antes de la eliminación
- Los registros de auditoría se conservarán durante el período legalmente requerido
- Las conversaciones con el agente IA serán anonimizadas para fines de mejora del servicio

El Negocio puede solicitar la eliminación anticipada de todos sus datos en cualquier momento desde el panel de configuración.`
        }
      ],
      responsibilities: {
        title: "7. Responsabilidades del Negocio",
        items: [
          "Garantizar que tiene base legal para tratar los datos de los contactos que proporciona",
          "Informar a sus clientes sobre el uso de servicios automatizados de comunicación",
          "Obtener el consentimiento necesario para comunicaciones de marketing",
          "Notificar a LokAlGrow cualquier solicitud de derechos de los interesados",
          "Mantener actualizados los datos de contacto proporcionados"
        ]
      },
      compliance: {
        title: "8. Cumplimiento Normativo",
        text: "Este DPA está diseñado para cumplir con el Reglamento General de Protección de Datos (RGPD) de la UE, la Ley Orgánica de Protección de Datos (LOPD-GDD) de España, y demás normativa aplicable en materia de protección de datos."
      },
      contact: {
        title: "9. Contacto del Delegado de Protección de Datos",
        text: "Para cualquier consulta relacionada con este DPA o el tratamiento de datos personales:",
        email: "dpo@lokalgrow.com"
      }
    },
    en: {
      title: "Data Processing Agreement (DPA)",
      subtitle: "Between LokAlGrow (Processor) and the Business (Data Controller)",
      lastUpdated: "Last updated: April 2026",
      sections: [
        {
          icon: FileText,
          title: "1. Definitions and Scope",
          content: `This Data Processing Agreement ("DPA") forms part of LokAlGrow's Terms of Service and establishes the parties' obligations regarding the processing of personal data.

- "Data Controller": The business using LokAlGrow services that determines the purposes and means of processing their customer data.
- "Processor": LokAlGrow, S.L., which processes personal data on behalf of the Controller.
- "Personal Data": Any information relating to an identified or identifiable natural person provided by the Controller through the platform.`
        },
        {
          icon: Database,
          title: "2. Data We Process",
          content: `In the context of reactivation and birthday club services, LokAlGrow processes:

- Contact names
- Phone numbers (WhatsApp/SMS)
- Email addresses (when provided)
- Birth dates (for VIP Club)
- AI agent interaction history
- Conversation and reservation status

This data is provided exclusively by the Business, who declares having a legal basis for its processing.`
        },
        {
          icon: Lock,
          title: "3. Security and Confidentiality",
          content: `LokAlGrow implements appropriate technical and organizational measures to ensure data security:

- Data encryption in transit (TLS 1.3) and at rest
- Restricted access through multi-factor authentication
- Logical data segregation by business
- Encrypted backups with 30-day retention
- Periodic security audits
- Staff with confidentiality agreements

Each campaign's conversations are isolated and accessible only by the owner Business and authorized LokAlGrow personnel.`
        },
        {
          icon: Users,
          title: "4. Data Subject Rights",
          content: `LokAlGrow will assist the Business in fulfilling its obligations regarding data subject rights:

- Right of access: We will provide data export upon request
- Right of rectification: The Business can update data from their dashboard
- Right of erasure: We implement immediate opt-out when a contact requests it
- Right to object: Contacts expressing unwillingness to be contacted are automatically marked as "Do Not Contact"

When a contact requests deletion of their data, LokAlGrow will process it within 72 hours.`
        },
        {
          icon: Shield,
          title: "5. Sub-processors",
          content: `LokAlGrow uses the following sub-processors to provide its services:

- Twilio Inc. (USA): SMS and WhatsApp sending - SOC 2 certified
- Anthropic (USA): Natural language processing - With standard contractual clauses
- MongoDB Atlas (EU/USA): Data storage - ISO 27001 certified
- Brevo (France): Email sending - GDPR compliant

International transfers are made under the EU Standard Contractual Clauses framework.`
        },
        {
          icon: Scale,
          title: "6. Duration and Termination",
          content: `This DPA will remain in effect while the Business uses LokAlGrow services.

Upon service termination:
- Contact data will be deleted within 90 days
- The Business may request a complete export before deletion
- Audit logs will be retained for the legally required period
- AI agent conversations will be anonymized for service improvement purposes

The Business may request early deletion of all their data at any time from the settings panel.`
        }
      ],
      responsibilities: {
        title: "7. Business Responsibilities",
        items: [
          "Ensure having a legal basis to process the contact data provided",
          "Inform their customers about the use of automated communication services",
          "Obtain necessary consent for marketing communications",
          "Notify LokAlGrow of any data subject rights requests",
          "Keep provided contact data updated"
        ]
      },
      compliance: {
        title: "8. Regulatory Compliance",
        text: "This DPA is designed to comply with the EU General Data Protection Regulation (GDPR), Spain's Organic Law on Data Protection (LOPD-GDD), and other applicable data protection regulations."
      },
      contact: {
        title: "9. Data Protection Officer Contact",
        text: "For any queries related to this DPA or personal data processing:",
        email: "dpo@lokalgrow.com"
      }
    }
  };

  const c = isSpanish ? content.es : content.en;

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-2xl mb-4">
            <Shield className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{c.title}</h1>
          <p className="text-gray-600">{c.subtitle}</p>
          <p className="text-sm text-gray-500 mt-2">{c.lastUpdated}</p>
        </div>

        {/* Main Sections */}
        <div className="space-y-8">
          {c.sections.map((section, idx) => (
            <div key={idx} className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <section.icon className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-3">{section.title}</h2>
                  <div className="text-gray-600 text-sm whitespace-pre-line leading-relaxed">
                    {section.content}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Responsibilities */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{c.responsibilities.title}</h2>
            <ul className="space-y-2">
              {c.responsibilities.items.map((item, idx) => (
                <li key={idx} className="flex items-start gap-2 text-gray-600 text-sm">
                  <span className="text-blue-500 mt-1">•</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Compliance */}
          <div className="bg-blue-50 rounded-2xl p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">{c.compliance.title}</h2>
            <p className="text-gray-600 text-sm">{c.compliance.text}</p>
          </div>

          {/* Contact */}
          <div className="bg-white rounded-2xl p-6 shadow-sm text-center">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">{c.contact.title}</h2>
            <p className="text-gray-600 text-sm mb-2">{c.contact.text}</p>
            <a 
              href={`mailto:${c.contact.email}`} 
              className="text-blue-600 hover:underline font-medium"
            >
              {c.contact.email}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataProcessingAgreement;
